import { useEffect, useRef, useState } from "react";
// import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import { Axios } from "api/axios";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import ThemeTabs from "components/Tabs/ThemeTabs";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import NoWarehousesMessage from "Common/Components/NoWarehousesMessage";
import Dispatch from "./Dispatch/List";
import IssueToLabour from "./IssueToLabour/List";
import ReadyForDispatch from "./ReadyForDispatch/List";
import StockTransfer from "./StockTransfer/List";

const DispatchContainer = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const prevTabRef = useRef<number | null>(null);
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("WarehouseDispatchTab")) || 1
  );

  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Use preserved table state hook with tabIndex for per-tab state preservation
  const tableState = usePreservedTableState({
    key: "dispatch",
    tabIndex: tab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection: "" | "asc" | "desc" | undefined = sorting[0]?.desc
    ? "desc"
    : sorting[0]
    ? "asc"
    : "";

  // 🔥 Warehouse search (optional) - stored separately as it's not part of table state
  const [warehouseIdSearch, setWarehouseIdSearch] = useState(
    sessionStorage.getItem("WarehouseIdDispatch") || ""
  );

  // Use custom hook to fetch and access permissions
  const { canView, canCreate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.dispatch
  );

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // ✅ Tab switch - reset state when tab changes
  const handleTabChange = (val: number) => {
    setTab(val);
    sessionStorage.setItem("WarehouseDispatchTab", val.toString());
  };

  // Reset state when tab changes (using useEffect to handle tab changes)
  // This ensures state is cleared when switching tabs, but preserved when navigating away and back
  useEffect(() => {
    // Only reset if tab actually changed (not on initial mount)
    // The hook will have re-initialized with the new tabIndex by this point
    if (prevTabRef.current !== null && prevTabRef.current !== tab) {
      // Clear the state for the new tab
      resetState();
    }
    prevTabRef.current = tab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]); // Only depend on tab, not resetState to avoid infinite loops

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  // Tabs configuration
  const tabs = [
    { label: "Ready for Dispatch" },
    { label: "Dispatch" },
    { label: "Stock Transfer" },
    { label: "Issue to Labour" },
  ];

  const handleExport = async () => {
    let exportApi = "";
    let filename = "";
    switch (tab) {
      case 1:
        exportApi = api.warehouse.dispatch.ready_for_dispatch.export_csv;
        filename = "ready_for_dispatch.csv";
        break;
      case 2:
        exportApi = api.warehouse.dispatch?.dispatch_done?.export_csv;
        filename = "dispatch_done.csv";
        break;
      case 3:
        exportApi = api.warehouse.dispatch.stock_transfer.export_csv;
        filename = "stock_transfer.csv";
        break;
      case 4:
        exportApi = api.warehouse.dispatch.issue_to_labour.listing;
        filename = "issue_to_labour.csv";
        break;
      default:
        showCustomSwal({
          icon: "info",
          title: "Invalid Tab",
          text: "Invalid tab selected.",
        });
        return;
    }

    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Build query params from filters
      const params = new URLSearchParams({
        page: String(pageIndex + 1),
        pageSize: String(pageSize),
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        // Use warehouse_id for stock transfer, dispatch_from_warehouse_id for others
        ...(tab === 2 || tab === 3 || tab === 4
          ? { warehouse_id: warehouseIdSearch }
          : { dispatch_from_warehouse_id: warehouseIdSearch }),
        ...filterValue.reduce((acc: any, f: any) => {
          if (typeof f.filterValue === "object" && f.filterValue !== null) {
            if (
              f.filterField === "dispatch_date" ||
              f.filterField === "created_at" ||
              f.filterField === "updated_at" ||
              f.filterField === "order_date"
            ) {
              acc[`${f.filterField}_from`] = f.filterValue.from_date;
              acc[`${f.filterField}_to`] = f.filterValue.to_date;
            }
            console.log("Detected");
          } else {
            acc[f.filterField] = f.filterValue;
          }
          return acc;
        }, {}),
        // ...columnFilters.reduce((acc: any, f: any) => {
        //   console.log("F",f)
        //    if (typeof f.filterValue === "object" && f.filterValue !== null) {
        //     if (f.filterField === "dispatch_date" || f.filterField === "created_at" || f.filterField === "updated_at") {
        //       acc[`${f.filterField}_from`] = f.filterValue.from_date;
        //       acc[`${f.filterField}_to`] = f.filterValue.to_date;
        //     }
        //     console.log("Detected")
        //   } else {
        //     acc[f.filterField] = f.filterValue;
        //   }
        //   return acc;
        // }, {}),
        export: "csv", // ensure backend knows to send CSV
      });

      console.log("Export API:", params.toString());

      const response = await Axios.get(`${exportApi}?${params.toString()}`, {
        responseType: "blob",
      });

      // Download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export successful.",
      });
    } catch (error: any) {
      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to export CSV.",
      });
      console.error("Export error:", error);
    }
  };

  useEffect(() => {
    const fetchInit = async () => {
      try {
        setLoading(true);
        const { payload } = await dispatch(fetchWarehouseDropdown());
        setWarehouseOptions(payload.data);

        // Check if stored warehouse is still valid (exists in current options)
        const storedWarehouse = sessionStorage.getItem("WarehouseIdDispatch");
        const isStoredWarehouseValid = storedWarehouse &&
          payload.data.some((warehouse: any) => warehouse.warehouse_id === storedWarehouse);

        if (storedWarehouse && isStoredWarehouseValid) {
          setWarehouseIdSearch(storedWarehouse);
        } else {
          // Clear invalid stored warehouse and auto-select first available
          sessionStorage.removeItem("WarehouseIdDispatch");
          setWarehouseIdSearch(payload.data[0]?.warehouse_id || "");
          sessionStorage.setItem(
            "WarehouseIdDispatch",
            payload.data[0]?.warehouse_id || ""
          );
        }
      } catch (error) {
        toast.error("Failed to fetch initial data");
      } finally {
        setLoading(false);
      }
    };
    fetchInit();
  }, [dispatch]);

  return (
    <ListingPageContainer>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                {/* Title */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={2}
                  xl={2}
                  className="mb-2 mb-lg-0"
                >
                  <h6 className="card-title mb-0">Dispatch</h6>
                </Col>

                {/* Right-aligned elements container */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={10}
                  xl={10}
                  className="mb-2 mb-lg-0"
                >
                  <Row className="g-2 justify-content-end align-items-center">
                    {/* Search Input Box */}
                    <Col xs={12} sm={12} md={6} lg={3} xl={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    {/* Warehouse Autocomplete */}
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={4}
                      className=""
                      style={{ minWidth: "200px" }}
                    >
                      <WarehouseAutocomplete
                        options={warehouseOptions}
                        value={warehouseIdSearch}
                        loading={loading}
                        showWarehouseType={true}
                        disableClearable
                        sessionStorageKey="WarehouseIdDispatch"
                        onChange={(warehouseId) => {
                          setWarehouseIdSearch(String(warehouseId));

                          // Reset filters and sorting when warehouse changes
                          setColumnFilters([]);
                          setFilterValue([]);
                          setSorting([]);
                          setPageIndex(0);
                        }}
                      />
                    </Col>

                    {/* Buttons */}
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg="auto"
                      xl="auto"
                      className="d-flex gap-2 align-items-center flex-wrap flex-lg-nowrap justify-content-start justify-content-lg-start"
                    >
                      {canView && (
                        <ActionButton
                          variant="light"
                          label="Export"
                          icon="bi bi-box-arrow-up"
                          handleClick={handleExport}
                        />
                      )}

                      {canCreate && (
                        <>
                          {tab === 4 && (
                            <ActionButton
                              label="Issue to Labour"
                              icon="bi bi-plus-circle"
                              navigateLink="/warehouse-dispatch/IssueToLabour/create-dispatch-labour"
                              variant="primary"
                            />
                          )}
                          {tab === 3 && (
                            <ActionButton
                              label="Create Stock Transfer"
                              icon="bi bi-plus-circle"
                              navigateLink="/warehouse-dispatch/stockTransfer/create-stock-transfer"
                              variant="primary"
                            />
                          )}
                        </>
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-3">
                {/* Tabs */}
                <Col xs={12}>
                  <ThemeTabs
                    tabs={tabs}
                    value={tab - 1}
                    onChange={(newValue) => handleTabChange(newValue + 1)}
                  />
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {warehouseOptions.length > 0 ? (
                <LoaderErrorWrapper loading={loading} error={null}>
                  {tab === 1 && (
                    <ReadyForDispatch
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 2 && (
                    <Dispatch
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 3 && (
                    <StockTransfer
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 4 && (
                    <IssueToLabour
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                </LoaderErrorWrapper>
              ) : (
                <NoWarehousesMessage />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default DispatchContainer;
